import React, { createContext, useState } from 'react';
import { Assessment } from '@exerai/vision-engine-web';
import { yupResolver } from '@hookform/resolvers/yup';
import { Control, FieldErrors, useForm, UseFormWatch, UseFormSetValue } from 'react-hook-form';
import * as yup from 'yup';
import { useHttpContext } from '@/common/hooks/HttpContext';
import { toasts } from '@/common/toasts';
import { Nullish } from '@/common/types';
import { DemographicsCategory } from '@/components/Scan/Outcomes/types/CriteriaCategory.enum';
import { OutcomeCategory } from '@/components/Scan/Outcomes/types/OutcomeCategory.enum';
import { AssessmentOutcome, OutcomeFactor } from '@/components/Scan/Outcomes/types/types';
import { outcomeUsesSidedResults } from '@/components/Scan/Outcomes/utils';
import { useAdminOutcomesContext } from '@/components/context/hooks/AdminOutcomesContext';
import { resultIsError } from '@/services/HttpService';

export const assessmentOutcomeSchema = yup.object().shape({
    type: yup.string().required(),
    // The unused half of the sided/single pair is null, so these must accept null.
    resultDefinitionId: yup.string().nullable(),
    leftResultDefinitionId: yup.string().nullable(),
    rightResultDefinitionId: yup.string().nullable(),
    demographicsRequired: yup.string().nullable(),
    source: yup.string(),
    tremorsScale: yup.string().nullable(),
    factors: yup.array().of(
        yup.object().shape({
            category: yup.string().required(),
            description: yup.string().nullable(),
            shortDescription: yup.string().nullable(),
            criteria: yup
                .array()
                .of(
                    yup.object().shape({
                        type: yup.string().required(),
                        units: yup.string().nullable(),
                        conditions: yup
                            .array()
                            .of(
                                yup.object().shape({
                                    comparator: yup.string().required(),
                                    value: yup.mixed<string | number>().required(),
                                }),
                            )
                            .required(),
                    }),
                )
                .required(),
        }),
    ),
});

type AssessmentOutcomeFormValues = {
    source: string;
    type: OutcomeCategory;
    demographicsRequired: string;
    resultDefinitionId: Nullish<string>;
    leftResultDefinitionId: Nullish<string>;
    rightResultDefinitionId: Nullish<string>;
    tremorsScale: Nullish<string>;
    factors: OutcomeFactor[];
};

interface IOutcomeBuilderContext {
    activeStep: number;
    assessment: Assessment;
    handleNext: () => void;
    handleBack: () => void;
    onSubmit: () => void;
    control: Control<AssessmentOutcomeFormValues>;
    errors: FieldErrors<AssessmentOutcomeFormValues>;
    getAssessmentOutcome: () => AssessmentOutcome;
    watch: UseFormWatch<AssessmentOutcomeFormValues>;
    setValue: UseFormSetValue<AssessmentOutcomeFormValues>;
    isNew: boolean;
}

export const OutcomeBuilderContext = createContext<IOutcomeBuilderContext | null>(null);

interface OutcomeBuilderProviderProps {
    children: JSX.Element | JSX.Element[];
    outcome: AssessmentOutcome;
    assessment: Assessment;
    organizationId: number;
    isNew: boolean;
    close: () => void;
}

export const OutcomeBuilderProvider = (props: OutcomeBuilderProviderProps) => {
    const { outcome, assessment, organizationId, close, children, isNew } = props;
    const { refresh } = useAdminOutcomesContext();

    const [activeStep, setActiveStep] = useState<0 | 1 | 2>(0);

    const { httpService } = useHttpContext();
    const { errorToast, successToast } = toasts;

    const {
        control,
        handleSubmit,
        watch,
        setValue,
        formState: { errors },
    } = useForm({
        resolver: yupResolver(assessmentOutcomeSchema),
        defaultValues: {
            source: outcome.source,
            type: outcome.type,
            demographicsRequired:
                outcome.demographicsRequired && outcome.demographicsRequired.length > 0 ? 'true' : 'false',
            resultDefinitionId: outcome.resultDefinitionId,
            leftResultDefinitionId: outcome.leftResultDefinitionId,
            rightResultDefinitionId: outcome.rightResultDefinitionId,
            tremorsScale: outcome.title,
            factors: outcome.factors,
        },
    });

    const formToEntity = (form: AssessmentOutcomeFormValues): AssessmentOutcome => {
        const {
            source,
            type,
            resultDefinitionId,
            leftResultDefinitionId,
            rightResultDefinitionId,
            tremorsScale,
            factors,
        } = form;

        const tremorsDescription = { UPDRS: 'Item 3.15', CRST: 'Part A' };
        // Keep the two shapes mutually exclusive: a sided outcome is defined by its left/right
        // pair, everything else by the single id. Writing all three would leave a stale id
        // behind and make the persisted record ambiguous about which side was scanned.
        const usesSidedResults = outcomeUsesSidedResults(type, assessment);
        const outcome: AssessmentOutcome = {
            assessmentId: assessment.id,
            type,
            source,
            resultDefinitionId: usesSidedResults ? null : resultDefinitionId,
            leftResultDefinitionId: usesSidedResults ? leftResultDefinitionId : null,
            rightResultDefinitionId: usesSidedResults ? rightResultDefinitionId : null,
            factors,
            demographicsRequired:
                form.demographicsRequired === 'true' ? [DemographicsCategory.AGE, DemographicsCategory.SEX] : null,
            title: tremorsScale,
            description: tremorsScale ? tremorsDescription[tremorsScale] : null,
        };

        return outcome;
    };

    const getAssessmentOutcome = (): AssessmentOutcome => {
        const form = watch();
        return formToEntity(form);
    };

    const handleSave = async (form: AssessmentOutcomeFormValues) => {
        const outcome = formToEntity(form);
        const res = await httpService.adminPutScanRiskFactorForAssessmentAndOrg(organizationId, assessment.id, outcome);
        if (!res || resultIsError(res)) {
            errorToast(`Error saving analysis configuration. ${res.message}`);
            return;
        } else {
            close();
            refresh();
            successToast(`Successfully updated analysis configuration.`);
        }
    };

    const onSubmit = handleSubmit(handleSave, (errors) => {
        // Without this the save button looks unresponsive: the click is swallowed by a
        // validation failure on a field no step displays.
        console.log('Validation errors:', errors);
        errorToast(`Analysis configuration is invalid: ${Object.keys(errors).join(', ')}`);
    });

    const handleNext = () => {
        switch (activeStep) {
            case 0:
                setActiveStep(1);
                break;
            case 1:
                setActiveStep(2);
                break;
            case 2:
                break;
            default:
                break;
        }
    };

    const handleBack = () => {
        switch (activeStep) {
            case 1:
                setActiveStep(0);
                break;
            case 2:
                setActiveStep(1);
                break;
        }
    };

    const outcomeBuilderContext = {
        activeStep,
        assessment,
        handleNext,
        handleBack,
        getAssessmentOutcome,
        control,
        errors,
        watch,
        setValue,
        onSubmit,
        isNew,
    };

    return <OutcomeBuilderContext.Provider value={outcomeBuilderContext}>{children}</OutcomeBuilderContext.Provider>;
};
